import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  Timestamp,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { firebaseOptimizer } from "../services/firebaseOptimizer";

/**
 * Action types for history logging
 */
export const ACTION_TYPES = {
  ASSIGNED: "assigned",
  RETURNED: "returned",
  DELETED: "deleted",
  AMOUNT_CHANGED: "amount_changed",
  SEEN: "seen",
  CREATED: "created",
  UPDATED: "updated",
  MADE_AVAILABLE: "made_available",
};

/**
 * Action type labels in Hebrew (object key order = History filter checklist order)
 */
export const ACTION_LABELS = {
  [ACTION_TYPES.ASSIGNED]: "הוחתם",
  [ACTION_TYPES.MADE_AVAILABLE]: "הוחזר לזמין",
  [ACTION_TYPES.RETURNED]: "זוכה",
  [ACTION_TYPES.AMOUNT_CHANGED]: "שינוי כמות",
  [ACTION_TYPES.SEEN]: "דוח צ",
  [ACTION_TYPES.CREATED]: "נוצר",
  [ACTION_TYPES.UPDATED]: "עודכן",
  [ACTION_TYPES.DELETED]: "נמחק",
};

/**
 * Custom hook for history/activity log management
 */
export const useHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef(null);
  const latestFetchOptionsRef = useRef(null);

  /**
   * Log an activity to history
   */
  const logActivity = useCallback(
    async ({
      userId,
      userName,
      actionType,
      itemId,
      itemName,
      serialNumber = null,
      details = {},
      metadata = {},
    }) => {
      try {
        const historyRef = collection(db, "history");
        const activityData = {
          timestamp: Timestamp.now(),
          userId,
          userName,
          actionType,
          itemId,
          itemName,
          serialNumber,
          details,
          metadata,
        };

        await addDoc(historyRef, activityData);
        return { success: true };
      } catch (e) {
        console.error("Error logging activity: ", e);
        return { success: false, error: e.message };
      }
    },
    []
  );

  const applyAccessFilter = useCallback(
    (historyData, { userId, userRole, unitPersonnelNumbers }) => {
      if (userRole === "childUser" && userId) {
        const unitSet = unitPersonnelNumbers
          ? new Set(unitPersonnelNumbers)
          : new Set();
        return historyData.filter(
          (item) =>
            item.userId === userId ||
            (item.details?.personalNumber &&
              unitSet.has(item.details.personalNumber)),
        );
      }
      if (userRole === "parentsUser" && userId) {
        return historyData.filter(
          (item) =>
            item.userId === userId || item.metadata?.userRole === "admin",
        );
      }
      return historyData;
    },
    [],
  );

  const buildHistoryQuery = useCallback(
    ({ actionType, startDate, endDate, limitCount, afterDoc = null }) => {
      const historyRef = collection(db, "history");
      let q = query(historyRef, orderBy("timestamp", "desc"));

      if (actionType) {
        q = query(q, where("actionType", "==", actionType));
      }
      if (startDate) {
        q = query(q, where("timestamp", ">=", Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where("timestamp", "<=", Timestamp.fromDate(endDate)));
      }
      if (afterDoc) {
        q = query(q, startAfter(afterDoc));
      }
      q = query(q, limit(limitCount));
      return q;
    },
    [],
  );

  /**
   * Fetch first page with filters and access control
   */
  const fetchHistory = useCallback(
    async ({
      userId = null,
      userRole = "childUser",
      actionType = null,
      startDate = null,
      endDate = null,
      limitCount = 1500,
      unitPersonnelNumbers = null,
    } = {}) => {
      const options = {
        userId,
        userRole,
        actionType,
        startDate,
        endDate,
        limitCount,
        unitPersonnelNumbers,
      };
      latestFetchOptionsRef.current = options;
      cursorRef.current = null;
      setHasMore(false);
      setLoadingMore(false);

      try {
        setLoading(true);
        setError(null);

        const q = buildHistoryQuery(options);
        const querySnapshot = await getDocs(q);
        const historyData = [];

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          historyData.push({
            id: docSnap.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
          });
        });

        const filteredData = applyAccessFilter(historyData, options).sort(
          (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
        );

        const lastDoc =
          querySnapshot.docs.length > 0
            ? querySnapshot.docs[querySnapshot.docs.length - 1]
            : null;
        cursorRef.current = lastDoc;
        setHasMore(querySnapshot.docs.length === limitCount);

        setHistory(filteredData);
        return { success: true, data: filteredData };
      } catch (e) {
        console.error("Error fetching history: ", e);
        setError(e.message);
        return { success: false, error: e.message };
      } finally {
        setLoading(false);
      }
    },
    [applyAccessFilter, buildHistoryQuery],
  );

  /**
   * Fetch next page and append to existing history
   */
  const loadMoreHistory = useCallback(async () => {
    const options = latestFetchOptionsRef.current;
    if (!options || !hasMore || loading || loadingMore) {
      return { success: true, data: history, hasMore };
    }

    try {
      setLoadingMore(true);
      const q = buildHistoryQuery({
        ...options,
        afterDoc: cursorRef.current,
      });
      const querySnapshot = await getDocs(q);
      const pageData = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        pageData.push({
          id: docSnap.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        });
      });

      const filteredPage = applyAccessFilter(pageData, options);
      const lastDoc =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;
      cursorRef.current = lastDoc;
      const more = querySnapshot.docs.length === options.limitCount;
      setHasMore(more);

      let nextHistory = [];
      setHistory((prev) => {
        const map = new Map(prev.map((item) => [item.id, item]));
        for (const item of filteredPage) {
          map.set(item.id, item);
        }
        nextHistory = Array.from(map.values()).sort(
          (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
        );
        return nextHistory;
      });

      return { success: true, data: nextHistory, hasMore: more };
    } catch (e) {
      console.error("Error loading more history: ", e);
      setError(e.message);
      return { success: false, error: e.message, hasMore };
    } finally {
      setLoadingMore(false);
    }
  }, [applyAccessFilter, buildHistoryQuery, hasMore, history, loading, loadingMore]);

  /**
   * Delete old history records (older than 3 months)
   */
  const deleteOldHistory = useCallback(async () => {
    try {
      const fiveMonthsAgo = new Date();
      fiveMonthsAgo.setMonth(fiveMonthsAgo.getMonth() - 5);

      const historyRef = collection(db, "history");
      const q = query(
        historyRef,
        where("timestamp", "<", Timestamp.fromDate(fiveMonthsAgo)),
        limit(500) // Process in batches
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = [];

      querySnapshot.forEach((docSnapshot) => {
        deletePromises.push(deleteDoc(doc(db, "history", docSnapshot.id)));
      });

      await Promise.all(deletePromises);
      return { success: true, deleted: deletePromises.length };
    } catch (e) {
      console.error("Error deleting old history: ", e);
      return { success: false, error: e.message };
    }
  }, []);

  /**
   * Get formatted action description
   */
  const getActionDescription = useCallback((activity) => {
    const d = activity.details || {};

    let actionLabel = ACTION_LABELS[activity.actionType] || activity.actionType;
    if (activity.actionType === ACTION_TYPES.RETURNED) {
      const employeeLinked = !!(
        (d.returnedFrom && String(d.returnedFrom).trim()) ||
        d.personalNumber
      );
      actionLabel = employeeLinked ? "הוחזר לזמין" : "זוכה";
    }

    let description = `${actionLabel}: ${activity.itemName}`;

    if (activity.serialNumber) {
      description += ` (מספר סידורי: ${activity.serialNumber})`;
    }

    switch (activity.actionType) {
      case ACTION_TYPES.ASSIGNED:
        if (d.assignedTo) {
          description += ` → ${d.assignedTo}`;
        } else if (d.personalNumber) {
          description += ` → מס׳ אישי: ${d.personalNumber}`;
        }
        if (d.quantity) {
          description += ` (כמות: ${d.quantity})`;
        }
        break;
      case ACTION_TYPES.RETURNED:
        if (d.returnedFrom) {
          description += ` מ-${d.returnedFrom}`;
        } else if (d.personalNumber) {
          description += ` מ-מס׳ אישי: ${d.personalNumber}`;
        }
        if (d.quantity) {
          description += ` (כמות: ${d.quantity})`;
        }
        break;
      case ACTION_TYPES.AMOUNT_CHANGED:
        if (
          activity.details.oldValue !== undefined &&
          activity.details.newValue !== undefined
        ) {
          description += ` (כמות: ${activity.details.oldValue} → ${activity.details.newValue})`;
        }
        break;
      case ACTION_TYPES.UPDATED:
        // Show all changes that were made
        if (activity.details.changes) {
          const changeParts = [];
          const changes = activity.details.changes;

          // Quantity change
          if (changes.quantity) {
            changeParts.push(
              `כמות: ${changes.quantity.old} → ${changes.quantity.new}`
            );
          }

          // Serial number change
          if (changes.serialNumber) {
            const oldSerial = changes.serialNumber.old || "ללא";
            const newSerial = changes.serialNumber.new || "ללא";
            changeParts.push(`מספר סידורי: ${oldSerial} → ${newSerial}`);
          }

          // Type (isOrganic) change
          if (changes.isOrganic) {
            const oldType = changes.isOrganic.old ? "אורגני" : "קווי";
            const newType = changes.isOrganic.new ? "אורגני" : "קווי";
            changeParts.push(`סוג: ${oldType} → ${newType}`);
          }

          // Item name change
          if (changes.itemName) {
            changeParts.push(
              `שם: ${changes.itemName.old} → ${changes.itemName.new}`
            );
          }

          // Item definition change
          if (changes.itemDefinitionId) {
            changeParts.push("סוג פריט שונה");
          }

          if (changeParts.length > 0) {
            description += ` (${changeParts.join(", ")})`;
          }
        } else if (
          activity.details.oldValue !== undefined &&
          activity.details.newValue !== undefined
        ) {
          // Backward compatibility for old format
          description += ` (${activity.details.oldValue} → ${activity.details.newValue})`;
        }
        break;
      case ACTION_TYPES.SEEN:
        if (d.clearedSeenAt) {
          description += " (בוטל סימון)";
        }
        break;
    }

    return description;
  }, []);

  return {
    history,
    loading,
    error,
    hasMore,
    loadingMore,
    logActivity,
    fetchHistory,
    loadMoreHistory,
    deleteOldHistory,
    getActionDescription,
    ACTION_TYPES,
    ACTION_LABELS,
  };
};
